import { Component, EventEmitter, Output, ViewChild, OnInit, Input, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { forkJoin } from 'rxjs';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../../shared/models/country';
import type { Language } from '../../../../shared/models/language';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import type { Column } from '../../../../shared/models/attribute.models';
import type { EntityList } from '../../../../shared/models/entity.models';
import type { ListResponse, Values } from '../../../../shared/models/listResponse.models';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { CommonModule } from '@angular/common';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { AddProtocolPatientComponent } from './add-patient/add-patient.component';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import type { AttributeData } from '../../../../shared/models/attribute.models';
import { ModuleHeaderComponent } from 'app/modules/shared/components/module-header/module-header.component';
import { ToastModule } from 'primeng/toast';

interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
  field: string;
  header: string;
  [key: string]: unknown;
}

interface FilterField {
  attributeId?: number;
  attributeCode: string;
  attributeIndex?: number;
  presetValues: Array<{
    labelKey: string;
    labelValue: string;
    parentLabelKey?: null | string;
  }>;
  [key: string]: unknown;
}

@Component({
  selector: 'app-patients',
  templateUrl: './patients.component.html',
  styleUrl: './patients.component.scss',
  standalone: true,
  imports: [TabViewModule,
    TranslateModule,
    MITableComponent,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    CommonModule,
    AccessProviderDirective,
    AddProtocolPatientComponent,
    CalendarModule,
    InputTextModule,
    ReactiveFormsModule,
    FormsModule,
    ModuleHeaderComponent,
    ToastModule
  ]
})
export class PatientsComponent implements OnInit {
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @ViewChild('fileInput') fileInput: ElementRef;
  @Output() emitTotalRecords = new EventEmitter();
  @Output() saveSuccess = new EventEmitter<void>();
  @Input() isAllowToAddProduct: boolean = false;
  @Input() protocolDates: any = { startDate: null, endDate: null };
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: string[] = [];
  cols: ColumnDefinition[] = [];
  moduleName: string = AppSettings.ENTITY_CODE.PROTOCOL_PATIENT;
  value: number = 50;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: any;
  attributeLabelsData: any;
  filterAttributeLabels: any;
  listColumns: Values;
  allColumns: any;
  pagination: { limit: number; offset: number; searchStr: string; defaultSortColumn?: string; defaultSortType?: string } = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: ""
  };
  patientPauseAttributeId;
  totalRecords: number = 0;
  tableData: Record<string, unknown>[] = [];
  buttons: Array<{ label: string; icon: string; command: () => void }> = [];
  country: Country;
  language: Language;
  entityData: EntityList = {
    actionStatus: "",
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [{ attributeId: null, attributeValue: '' }],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    status: 'both',
  };
  routePath: MenuItem[] = [];
  home: MenuItem = {};
  isModal: boolean = false;
  selectedColumns!: Column[];
  attributeData: AttributeData;
  data: unknown;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  columnsData: unknown;
  bulkDeleteLabel: string = this.cs.getLabel('manage_users.delete');
  deleteRoleHeaderMsg: string;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  filterFieldsArray: FilterField[] = [];
  isEntity: boolean = true;
  entityName: string;
  currentRow: Record<string, unknown>;
  filterList: FilterField[] = [];
  entityDataForRole: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: '',
    actionStatus: '',
    status: 'both',
  };
  miIcons = AppIcons;
  showAddDialog: boolean = false;
  patientId: string;
  protocolId: string;
  patientAttributeId: number;
  pausePatientHeader: string;
  showPauseDialog = false;
  minDate: Date;
  minStartDate: Date = new Date();
  maxFromDate: Date;
  maxEndDate: Date;
  fromDate: Date;
  endDate: Date;
  totalDays: number;
  pauseForm: FormGroup;
  patientStatusAttributeId: string;
  patientFromDateAttributeId: string;
  patientEndDateAttributeId: string;
  deleteRoleMsg: string;
  isUpdatingPause: boolean = false;
  siteAttributeId: string;

  constructor(
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private configService: ConfigService,
    private permissionAccessService: PermissionAccessService,
    private loaderService: LoaderService,
    private entityService: EntityService,
    private restApiService: RestApiService,
    private metaDataService: MetaDataService,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) { }

  ngOnInit() {
    this.retrieveProtocolId();
    this.setUpRoutePath();
    this.setUpRowItems();
    this.initializeOtherVariablesAndAPI();
    this.setModuleHeaderSecondaryBtnMenuItems();
    this.getMetaData();
    if (this.isAllowToAddProduct) {
      this.isAllowToAddProduct = this.permissionAccessService.hasAccess('PROTOCOL_PATIENTS', '', 'ADD', '');
    }
  }

  retrieveProtocolId() {
    this.route.queryParams.subscribe(params => {
      this.protocolId = params?.id;

    });
  }

  setModuleHeaderSecondaryBtnMenuItems() {
    this.moduleHeaderSecondaryBtnMenuItems = [
      {
        label: this.cs.getLabel('patient.lbl_download_patient'),
        icon: `${AppIcons.UPLOAD} wh-16`,
        command: () => {
          document.getElementById('patientFileUploadInput')?.click();
        }
      },
      {
        label: this.cs.getLabel('patient.lbl_download_template'),
        icon: `${AppIcons.DOWNLOAD2} wh-16`,
        command: () => {
          this.entityService.downloadImportTemplate(AppSettings.ENTITY_CODE.PROTOCOL_PATIENT).subscribe(
            (result: any) => {
              const a = document.createElement('a');
              a.href = URL.createObjectURL(result);
              a.download = 'Import Patient Template.xlsx';
              a.click();
            });
        }
      },
      {
        label: this.cs.getLabel('patient.lbl_download_guide'),
        icon: `${AppIcons.DOWNLOAD2} wh-16`,
        command: () => {
          this.entityService.downloadImportGuide(AppSettings.ENTITY_CODE.PROTOCOL_PATIENT).subscribe(result => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(result);
            a.download = 'Import Patient Guide.xlsx';
            a.click();
          });
        }
      },
    ];
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {

      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.patientAttributeId = this.cs.getAttributeId('protocol_entity_id', advancedFilter);
        this.entityData.filters[0].attributeId = this.patientAttributeId;
        this.entityData.filters[0].attributeValue = this.protocolId;
        this.setPatientFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setPatientTableView(defaultTableView);
      }
      const patientPauseView = JSON.parse(res.patientPauseView.responseBody);
      if (patientPauseView) {
        this.patientFromDateAttributeId = patientPauseView.tabs[0].groups[0].fields.find(field => field.attributeCode === 'pause_from_date')?.attributeId;
        this.patientEndDateAttributeId = patientPauseView.tabs[0].groups[0].fields.find(field => field.attributeCode === 'pause_to_date')?.attributeId;
        this.patientPauseAttributeId = patientPauseView.tabs[0].groups[0].fields.find(field => field.attributeCode === 'patient_pause')?.attributeId;
      }
      this.cs.applyFilterToColumn(defaultTableView, sort)
      this.getData();

    });
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PATIENT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PATIENT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PATIENT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL_PATIENT}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.PATIENT_PAUSE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.PATIENT_PAUSE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  setPatientFilterView(filterResponse) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse);
    this.filterList = this.cs.getColumns(filterResponse);
    this.filterList = this.filterList.filter(item => item.attributeCode === 'patient_status');
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
  }

  setPatientTableView(res) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(this.data);
    this.allColumns = this.cs.getColumns(this.data);
    this.allColumns = this.allColumns.filter(col => !['pause_from_date', 'pause_to_date'].includes(col.attributeCode));
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `protocol_patient.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });
    this.patientStatusAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'patient_status')?.attributeId;
    this.siteAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'site_entity_id')?.attributeId;

    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  initializeOtherVariablesAndAPI() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.btnLabel = this.cs.getLabel('patient.lbl_add_patient');
    this.pausePatientHeader = this.cs.getLabel('patient.lbl_pause_patient_participation');
    this.bulkDeleteLabel = this.cs.getLabel('patient.lbl_delete_patient');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));

    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteRoleMsg = this.cs.getLabel('patient.confirm_delete_msg');
    this.deleteRoleHeaderMsg = this.cs.getLabel('patient.delete_patient');
    this.getBulkUpdateAttributeData();
  }

  private setUpRoutePath() {
    this.routePath = [
      { label: this.cs.getLabel('patient.lbl_route_path') },
    ];
  }

  private setUpRowItems() {
    const isPause = this.permissionAccessService.hasAccess('PROTOCOL_PATIENTS', '', 'PAUSE', '');
    const isChangeTrial = this.permissionAccessService.hasAccess('PROTOCOL_PATIENTS', '', 'OFF_TRIAL', '');
    const isDelete = this.permissionAccessService.hasAccess('PROTOCOL_PATIENTS', '', 'DELETE', '');
    this.rowItems = [
      {
        label: this.cs.getLabel('patient.lbl_edit'),
        icon: `${AppIcons.EDIT_2} wh-122`,
        iconClass: 'bulk-update-icon',
        value: 'Edit',
        command: () => {
          this.onUpdatePatient(this.currentRow.id as string);
        }
      },
      ...(isPause ? [{
        label: this.cs.getLabel('patient.lbl_pause'),
        icon: `${AppIcons.CIRCLE_PAUSED} wh-122`,
        iconClass: 'bulk-update-icon',
        value: 'Pause',
        command: () => {
          this.onShowPauseDialog();
        }
      }] : []),
      ...(isPause ? [{
        label: this.cs.getLabel('patient.lbl_remove_pause'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-122`,
        iconClass: 'bulk-update-icon',
        value: 'Remove Pause',
        command: () => {
          this.onRemovePause();
        }
      }] : []),
      { separator: true },
      ...(isChangeTrial ? [{
        label: this.cs.getLabel('patient.lbl_on_trial'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-122`,
        value: 'On Trial',
        iconClass: 'bulk-update-icon',
        command: () => {
          this.changeStatus('On Trial');
          this.saveSuccess.emit();
        }
      }] : []),
      ...(isChangeTrial ? [{
        label: this.cs.getLabel('patient.lbl_off_trial'),
        icon: `${AppIcons.OFF_OUTLINE_CLOSE} wh-122`,
        iconClass: 'bulk-update-icon',
        value: 'Off Trial',
        command: () => {
          this.changeStatus('Off Trial');
          this.saveSuccess.emit();
        }
      }] : []),
      { separator: true },
      ...(isDelete ? [{
        label: this.cs.getLabel('lbl_delete'),
        icon: `${AppIcons.BASIC_DELETE} wh-122`,
        iconClass: 'bulk-update-icon',
        value: 'On Trial',
        command: () => {
          this.tableRef.delete(AppSettings.DELETE_TYPE.ROW);
          this.saveSuccess.emit();
        }
      }] : []),
    ];
  }

  getBulkUpdateAttributeData() {
    this.attributeData = {
      attributeLabels: {},
      entityCode: '',
      tenantId: '',
      languageId: '',
      countryId: '',
      viewCode: '',
      tabs: [
        {
          tabCode: '',
          tabIndex: 0,
          groups: [
            {
              code: '',
              index: 0,
              entityRelationshipConfigId: null,
              fields: [
                {
                  attributeCode: 'user_status',
                  attributeValue: null,
                  presetValues: [
                    { labelValue: 'Active', labelKey: 'Active', parentLabelKey: null },
                    { labelValue: 'Inactive', labelKey: 'Inactive', parentLabelKey: null },
                  ]
                } as any
              ]
            }
          ]
        }
      ]
    } as any;
    this.updateFilterFieldsArray(this.attributeData.tabs[0]?.groups[0]?.fields as unknown as Array<{ attributeCode: string;[key: string]: unknown }>);
  }

  private updateFilterFieldsArray(fields: Array<{ attributeCode: string;[key: string]: unknown }>) {
    this.filterFieldsArray = fields.map(field => ({
      attributeId: (field as any).attributeId || 0,
      attributeIndex: (field as any).attributeIndex || 0,
      attributeCode: field.attributeCode,
      presetValues: [
        { labelKey: "Active", labelValue: "Active" },
        { labelKey: "Inactive", labelValue: "Inactive" }
      ]
    }));
  }

  getTableView() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL_PATIENT, AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW, false).subscribe(res => {
      if (res) {
        this.searchEntity();
      }
    });
  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView()
    })
  }

  searchEntity() {
    this.entityData.countryCode = this.country[0].countryCode;


    this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL_PATIENT, this.entityData, false).subscribe((res: ListResponse) => {
      this.listColumns = res?.data[0]?.values;
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;
      this.emitTotalRecords.emit(res?.count);
      this.tableData = [];
      res.data?.forEach(data => {
        const siteEntityId = data.relatedData?.map(ele => {
          return ele.id
        })
        const siteName = data.relatedData?.map(ele => {
          return ele.values.site_name
        })
        data.values.site_entity_id = siteName;
        data.values.siteEntityId = siteEntityId;
        this.tableData.push({
          id: data.id,
          ...data.values,
        });
      });
      this.loaderService.hide();
    });
    this.saveSuccess.emit();
  }

  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  onFilterValueChange(event) {
    const protocolFilter = this.entityData.filters.find(filter => filter.attributeId === this.patientAttributeId);
    this.entityData.filters = protocolFilter ? [protocolFilter] : [];

    for (const [key, value] of Object.entries(event)) {
      const attributeValue = value;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;

      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.entityData.offset = event.first;
    this.searchEntity();
  }

  onSortData(sortColumnDetails) {

    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.entityData.limit = event;
    this.searchEntity();
  }

  update() {
    this.onUpdatePatient(this.currentRow.id as string);
  }

  onUpdatePatient(userId) {
    this.patientId = userId;
    this.showAddDialog = true;
  }

  makePatientActiveInactive(currentRow) {
    const isActive = currentRow.active === AppSettings.USERS_STATUS.ACTIVE;
    const headerMsg = isActive ? this.cs.getLabel('patient.inactive_user_header_message') : this.cs.getLabel('patient.active_user_header_message');
    const bodyMsg = isActive ? this.cs.getLabel('patient.inactive_status_message') : this.cs.getLabel('patient.active_status_message')
    const acceptLabel = isActive ? this.cs.getLabel('patient.lbl_deactivate') : this.cs.getLabel('patient.lbl_activate');
    this.confirmationService.confirm({
      header: headerMsg,
      message: bodyMsg,
      acceptLabel,
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectLabel: this.cs.getLabel('patient.cancel'),
      rejectButtonStyleClass: 'bg-white text-color',
      acceptButtonStyleClass: isActive ? 'bg-red-500' : 'bg-green-500',
      accept: () => {
        this.tableRef.changeStatusOfEntity();
      },
      reject: () => {
      }
    });

  }

  onChangeStatus(currentRow) {
    const newStatus = currentRow.active === AppSettings.USERS_STATUS.ACTIVE ? false : true;
    return newStatus;
  }

  private showSuccessMessage(labelKey) {
    this.messageService.add({
      key: 'tst',
      severity: 'success',
      summary: 'Successful',
      detail: this.cs.getLabel(labelKey),
    });
  }

  private showErrorMessage(labelKey) {
    this.messageService.add({
      key: 'tst',
      severity: 'error',
      summary: 'Error',
      detail: this.cs.getLabel(labelKey)
    });
  }

  setCurrentRowData(event) {
    this.currentRow = event;
    const status = Object.entries(event?.patient_status)[0][0];
    this.setActionOptionsState(status);
  }

  setActionOptionsState(data) {
    const hasPauseDates = !!(this.currentRow?.pause_from_date && this.currentRow?.pause_to_date);
    const protocolStatus = this.currentRow?.protocol_status;
    const isProtocolCompleted = protocolStatus && typeof protocolStatus === 'object' && 'completed' in protocolStatus && protocolStatus.completed === true;

    if (isProtocolCompleted || !this.isAllowToAddProduct) {
      this.rowItems = [];
      return;
    }

    this.rowItems.forEach(rowItem => {
      if (rowItem.value === 'Edit') {
        rowItem.disabled = data !== 'On Trial';
      } else if (rowItem.value === 'Pause') {
        rowItem.disabled = data === 'Off Trial';
        rowItem.label = hasPauseDates ? this.cs.getLabel('patient.lbl_update_pause') : this.cs.getLabel('patient.lbl_pause');
      } else if (rowItem.value === 'Remove Pause') {
        rowItem.disabled = !hasPauseDates || data === 'Off Trial';
      } else {
        rowItem.disabled = rowItem.value === data;
      }
    })
  }

  onAddPatient() {
    const isAdd = this.permissionAccessService.hasAccess('PROTOCOL_PATIENTS', '', 'ADD', '');
    if (isAdd) {
      this.showAddDialog = true;
    }
  }

  hidePopup(event) {
    if (event) {
      this.searchEntity();
      this.saveSuccess.emit();
    }
    this.patientId = null;
    this.showAddDialog = false;
  }

  onPatientDelete(event) {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: country[0].countryCode,
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL_PATIENT,
      entityIds: [
        event
      ]
    };
    this.entityService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'protocol_patient', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'protocol_patient', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }

  changeStatus(status) {
    const isActive = status === 'On Trial' ? true : false;


    const hasPauseDates = !!(this.currentRow?.pause_from_date && this.currentRow?.pause_to_date);
    if (hasPauseDates) {
      this.messageService.add({
        key: 'tst',
        severity: 'warn',
        summary: 'Warning',
        detail: this.cs.getLabel('patient.msg_remove_pause_before_on_trial')
      });
      return;
    }


    this.confirmationService.confirm({
      header: this.cs.getLabel('patient.lbl_change_trial_status'),
      message: this.cs.getLabel(isActive ? 'patient.msg_change_trial_to_on_trial' : 'patient.msg_change_trial_to_off_trial'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: this.cs.getLabel('lbl_yes'),
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const requestBody = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.patientStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.entityService.saveAttributeData(requestBody.entityCode, this.currentRow.id as string, requestBody).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: status === 'On Trial' ?
                this.cs.getLabel('patient.msg_patient_status_changed_to_on_trial') :
                this.cs.getLabel('patient.msg_patient_status_changed_to_off_trial')
            });
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          }
        });
      },
      reject: () => {
      }
    });
  }

  closePauseDialog(arg0: boolean) {
    this.totalDays = null;
    this.isUpdatingPause = false;
    this.showPauseDialog = arg0;
  }

  onShowPauseDialog() {
    this.initializeForm();

    const hasPauseDates = !!(this.currentRow?.pause_from_date && this.currentRow?.pause_to_date);
    this.isUpdatingPause = hasPauseDates;

    // Set max end date to protocol end date if available
    if (this.protocolDates?.endDate) {
      this.maxEndDate = new Date(this.protocolDates.endDate);
    }

    if (hasPauseDates) {
      this.pausePatientHeader = this.cs.getLabel('patient.lbl_update_pause_patient_participation');
      const fromDate = this.currentRow.pause_from_date;
      const toDate = this.currentRow.pause_to_date;

      if (fromDate && toDate) {
        const fromDateObj = typeof fromDate === 'number' ? new Date(fromDate as number) : new Date(fromDate as string);
        const toDateObj = typeof toDate === 'number' ? new Date(toDate as number) : new Date(toDate as string);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.minStartDate = fromDateObj < today ? fromDateObj : today;

        this.pauseForm.patchValue({
          fromDate: fromDateObj,
          endDate: toDateObj
        });

        setTimeout(() => {
          this.onDateSelect();
        }, 0);
      }
    } else {
      this.pausePatientHeader = this.cs.getLabel('patient.lbl_pause_patient_participation');
      this.minStartDate = new Date();
    }

    this.showPauseDialog = true;
    this.pauseForm.markAsPristine();
    this.pauseForm.markAsUntouched();

    setTimeout(() => {
      const hiddenInput = document.querySelector('#hiddenInputPause') as HTMLInputElement;
      if (hiddenInput) {
        hiddenInput.focus();
      }
    }, 100);
  }

  onRemovePause() {
    this.confirmationService.confirm({
      header: this.cs.getLabel('patient.lbl_remove_pause_patient'),
      message: this.cs.getLabel('patient.msg_remove_pause_patient_participation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: this.cs.getLabel('lbl_yes'),
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const requestBody = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [
            {
              attributeId: this.patientFromDateAttributeId,
              attributeValue: null
            },
            {
              attributeId: this.patientEndDateAttributeId,
              attributeValue: null
            },
            {
              attributeId: this.patientPauseAttributeId,
              attributeValue: false
            }
          ],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.PATIENT_PAUSE_VIEW,
        };

        this.entityService.saveAttributeData(requestBody.entityCode, this.currentRow.id as string, requestBody).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('patient.msg_pause_removed_successfully')
            });
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          }
        });
      },
      reject: () => {
      }
    });
  }

  onDateSelect() {
    if (this.pauseForm.controls.fromDate.value) {
      this.minDate = this.pauseForm.controls.fromDate.value;
    } else {
      this.minDate = null;
    }
    if (this.pauseForm.valid) {
      const timeDiff = new Date(this.pauseForm.controls.endDate.value).getTime() - new Date(this.pauseForm.controls.fromDate.value).getTime();
      this.totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1;
    } else {
      this.totalDays = null;
    }
  }

  onPausePatient() {
    if (this.pauseForm.invalid) return this.pauseForm.markAllAsTouched();

    const fromDate = new Date(this.pauseForm.controls.fromDate.value);
    fromDate.setHours(0, 0, 0, 0);

    const endDate = new Date(this.pauseForm.controls.endDate.value);
    endDate.setHours(23, 59, 59, 999);

    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language?.[0]?.langCode || 'en',
      data: [
        {
          attributeId: this.patientFromDateAttributeId,
          attributeValue: fromDate.getTime()
        },
        {
          attributeId: this.patientEndDateAttributeId,
          attributeValue: endDate.getTime()
        },
        {
          attributeId: this.patientPauseAttributeId,
          attributeValue: true
        }
      ],
      relationshipData: [],
      viewCode: AppSettings.VIEW_CODE.PATIENT_PAUSE_VIEW,
    };
    this.entityService.saveAttributeData(requestBody.entityCode, this.currentRow.id as string, requestBody).subscribe({
      next: () => {
        const successMessage = this.isUpdatingPause
          ? this.cs.getLabel('patient.msg_pause_updated_successfully')
          : `Patient paused for ${this.totalDays} day(s) successfully`;

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: successMessage
        });
        setTimeout(() => {
          this.closePauseDialog(false);
          this.searchEntity();
        }, 300);
      }
    });
  }

  initializeForm(): void {
    this.pauseForm = this.fb.group({
      fromDate: ['', [Validators.required]],
      endDate: ['', [Validators.required]]
    },
      {
        validators: [this.validateDates('fromDate', 'endDate'), this.validateProtocolEndDate('endDate')],
      });
  }

  validateDates(fromKey: string, toKey: string): ValidatorFn {
    return (group: AbstractControl): { [key: string]: unknown } | null => {
      const fromDate = group.get(fromKey)?.value;
      const toDate = group.get(toKey)?.value;

      if (fromDate && toDate && fromDate > toDate) {
        return { endDateInvalid: true };
      }

      return null;
    };
  }

  validateProtocolEndDate(endDateKey: string): ValidatorFn {
    return (group: AbstractControl): { [key: string]: unknown } | null => {
      const endDate = group.get(endDateKey)?.value;

      if (endDate && this.protocolDates?.endDate) {
        const pauseEndDate = new Date(endDate);
        const protocolEndDate = new Date(this.protocolDates.endDate);

        if (pauseEndDate > protocolEndDate) {
          return { endDateExceedsProtocol: true };
        }
      }

      return null;
    };
  }

  onPatientFileUpload(event: any, docType: string): void {

    const file = event?.target?.files[0];
    if (!file) return;

    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: country[0].countryCode,
      uploadType: docType,
      originalFilename: file.name
    };

    this.cs.generateUploadFileSignedUrl(requestBody).subscribe((data: any) => {
      const urlForFileUpload = data?.signedUrl;
      if (urlForFileUpload) {
        this.cs.uploadFile(file, urlForFileUpload, data.headers).subscribe(() => {
          const reqBody = {
            forTenantCode: this.configService.getForTenantCode(),
            countryCode: country[0].countryCode,
            uploadType: docType,
            originalFilename: file.name,
            fileName: data.fileName,
            createdAt: data.createdAt,
            signedToken: data.signedToken
          };

          this.cs.verifyUploadedFile(reqBody, docType).subscribe((verifyResult: any) => {
            const fileId = verifyResult[0]?.resourceId;
            if (!fileId) {
              return;
            }

            const payload = {
              forTenantCode: this.configService.getForTenantCode(),
              protocolId: this.protocolId,
              fileId
            }

            this.cs.bulkImportPatients(AppSettings.ENTITY_CODE.PROTOCOL_PATIENT, payload).subscribe((verifyResult: any) => {
              const message = verifyResult?.message;
              this.messageService.add({ key: "fileupload", severity: 'success', summary: 'Success', detail: message });
              this.searchEntity();
              if (this.fileInput) {
                this.fileInput.nativeElement.value = '';
              }
            });

          }, () => {
            this.messageService.add({ key: "fileupload", severity: 'error', summary: 'Error', detail: this.cs.getLabel('lbl_bulk_import_error') });
            if (this.fileInput) {
              this.fileInput.nativeElement.value = '';
            }
          });
        });
      }
    });
  }

}


